import { setLocaleData } from '@wordpress/i18n';
import Popup from 'a11y/Popup';
import MenuItem from 'a11y/MenuItem';
import keyCodes from './lib/keyCodes';
import { rovingTabIndex } from './lib/rovingTabIndex';

/**
 * Menu class for managing a menu's top-level keyboard interactions.
 *
 * @param {HTMLElement} menu The menu <ul>
 */
export default class Menu {
  /**
   * Start the component
   */
  constructor(menu) {
    /**
     * The default string description for this object.
     *
     * @type {string}
     */
    this.stringDescription = 'Menu';

    /**
     * The menu <ul>
     * @type {HTMLElement}
     */
    this.menu = menu;

    /**
     * Will hold menu popups.
     * @type {Array}
     */
    this.popups = [];

    // Register text domain
    setLocaleData({ '': {} }, 'sciencenews');

    // Bind class methods.
    this.manageState = this.manageState.bind(this);
    this.handleMenuBarKeydown = this.handleMenuBarKeydown.bind(this);
    this.handleMenuBarClick = this.handleMenuBarClick.bind(this);
    this.destroy = this.destroy.bind(this);

    this.init();
  }

  /**
   * Get the string description for this object.
   * E.x., Menu.toString() === '[object Menu]'
   *
   * @return {string}
   */
  get [Symbol.toStringTag]() {
    return this.stringDescription;
  }

  /**
   * Manage menu activeDescendant state.
   *
   * @param {Object} newState The new state.
   */
  manageState(newState) {
    this.state = Object.assign(this.state, newState);

    if (Object.prototype.hasOwnProperty.call(newState, 'activeDescendant')) {
      const { activeDescendant } = this.state;

      rovingTabIndex(this.menuBarItems, activeDescendant);
      activeDescendant.focus();
    }
  }

  /**
   * Collect top-level menu items and set up event handlers.
   */
  init() {
    // Set the menu role.
    this.menu.setAttribute('role', 'menu');

    // Adds a circular self-reference for external access.
    this.menu.menu = this;

    this.menuItemsCollection = this.menu.children;
    this.menuItemsArray = Array.prototype.slice.call(this.menuItemsCollection);

    // Collect menu links.
    this.menuBarItems = this.menuItemsArray.reduce((acc, item) => {
      const itemLink = item.firstElementChild;

      if (null !== itemLink && 'A' === itemLink.nodeName) {
        return [...acc, itemLink];
      }

      return acc;
    }, []);

    /**
     * The number of menu items.
     * @type {Number}
     */
    this.menuLength = this.menuBarItems.length;

    // Set menu link attributes and event listeners.
    this.menuBarItems.forEach((link, index) => {
      link.parentElement.setAttribute('role', 'presentation');

      // Add size and position attributes.
      link.setAttribute('role', 'menuitem');
      link.setAttribute('aria-setsize', this.menuLength);
      link.setAttribute('aria-posinset', index + 1);

      link.addEventListener('keydown', this.handleMenuBarKeydown);
      link.addEventListener('click', this.handleMenuBarClick);
    });

    /**
     * The index of the last menubar item
     * @type {Number}
     */
    this.lastIndex = (this.menuLength - 1);

    // Save the menu's first and last items.
    [this.firstItem] = this.menuBarItems;
    this.lastItem = this.menuBarItems[this.lastIndex];

    /**
     * Set initial state.
     * @type {Object}
     */
    this.state = {
      activeDescendant: this.firstItem,
    };

    // Initialize popups for nested lists.
    this.popups = this.menuBarItems.reduce((acc, controller) => {
      const target = controller.nextElementSibling;

      if (null !== target && 'UL' === target.nodeName) {
        const popup = new Popup({ controller, target });

        target.addEventListener('keydown', this.handleListKeydown);
        const subList = new MenuItem(target);
        // Save the list's previous sibling.
        subList.previousSibling = controller;

        return [...acc, popup];
      }

      return acc;
    }, []);

    // Set up initial tabindex.
    rovingTabIndex(
      this.menuBarItems,
      this.state.activeDescendant,
    );
  }

  /**
   * Handle keydown events on the menuList element.
   *
   * @param {Object} event The event object.
   */
  handleMenuBarKeydown(event) {
    const { LEFT, RIGHT, DOWN } = keyCodes;
    const { keyCode } = event;
    const { activeDescendant } = this.state;
    const activeIndex = this.menuBarItems.indexOf(activeDescendant);

    if ([LEFT, RIGHT].includes(keyCode)) {
      event.stopPropagation();
      event.preventDefault();

      // Determine the direction.
      let nextIndex = (keyCode === LEFT)
        ? activeIndex - 1
        : activeIndex + 1;

      // Move to the end if we're moving to the previous child from the first child.
      if (LEFT === keyCode && 0 > nextIndex) {
        nextIndex = this.lastIndex;
      }

      // Move to first child if we're at the end.
      if (RIGHT === keyCode && this.lastIndex < nextIndex) {
        nextIndex = 0;
      }

      this.manageState({
        activeDescendant: this.menuBarItems[nextIndex],
      });
    } else if (DOWN === keyCode) {
      // Open the popup if it exists and is not expanded.
      if ('[object Popup]' === activeDescendant?.popup?.toString()) {
        event.stopPropagation();
        event.preventDefault();

        const { popup } = activeDescendant;

        if (! popup.state.expanded) {
          popup.setExpandedState(true);
        }

        popup.firstItem.focus();
      }
    }
  }

  /**
   * Update the active descendant when the item is clicked.
   *
   * @param  {Object} event The event object.
   */
  handleMenuBarClick(event) {
    this.manageState({
      activeDescendant: event.target,
    });
  }

  /**
   * Recursively destroy Menu, MenuItems, and Popups.
   */
  destroy() {
    // Remove the menu role.
    this.menu.removeAttribute('role');

    this.menuItemsCollection = this.menu.children;
    this.menuItemsArray = Array.prototype.slice.call(this.menuItemsCollection);

    this.menuBarItems.forEach((link) => {
      link.parentElement.removeAttribute('role');

      // Remove size and position attributes.
      link.removeAttribute('role');
      link.removeAttribute('aria-setsize');
      link.removeAttribute('aria-posinset');

      link.removeEventListener('keydown', this.handleMenuBarKeydown);
      link.removeEventListener('click', this.handleMenuBarClick);
    });

    // Destroy nested components.
    if (0 < this.popups.length) {
      this.popups.forEach((popup) => {
        if (
          undefined !== popup.target.menuItem
          && '[object MenuItem]' === popup?.target?.menuItem?.toString()
        ) {
          popup.target.menuItem.destroy();
        }
        popup.destroy();
      });
    }

    // Allow tabbing to all menubar items
    this.menuBarItems.forEach((menuBarItem) => {
      menuBarItem.removeAttribute('tabindex');
    });
  }
}
